import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { StatCard } from '../primitives';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { ACCENTS } from '../../lib/theme';

/**
 * Invoicing — the client's sales invoices, read live from Xero by the client's
 * Xero ContactID (legacy Backend_invoicing::getInvoice). Nothing is stored
 * locally; Xero is the record.
 *
 * One Australian financial year (1 July – 30 June) is loaded at a time, filtered
 * by Xero, because a large client has over a thousand invoices. The cards and
 * the table cover the selected year; search narrows the table only.
 *
 * Invoices raised from Outstanding Invoicing → Create Invoice also have a copy
 * saved by the portal (the lines as sent and Xero's PDF of the draft). Those
 * are flagged in the table and listed under it, each with its PDF.
 */

/** Financial year ending 30 June of `endYear`, e.g. 2027 = 1 Jul 2026 – 30 Jun 2027. */
interface Period {
    readonly key: string;
    readonly label: string;
    readonly from?: string;
    readonly to?: string;
}

function financialYear(endYear: number): Period {
    return {
        key: `fy${String(endYear)}`,
        label: `FY ${String(endYear - 1)}–${String(endYear).slice(2)} (1 Jul ${String(endYear - 1)} – 30 Jun ${String(endYear)})`,
        from: `${String(endYear - 1)}-07-01`,
        to: `${String(endYear)}-06-30`,
    };
}

/** The financial year containing today, in Melbourne. */
function currentFinancialYearEnd(): number {
    const [y, m] = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Australia/Melbourne',
        year: 'numeric',
        month: '2-digit',
    })
        .format(new Date())
        .split('-')
        .map(Number);
    return (m ?? 1) >= 7 ? (y ?? 0) + 1 : (y ?? 0);
}

const ALL_YEARS: Period = { key: 'all', label: 'All years' };

/** Financial year (by its 30 June end) that an invoice date falls in. */
function financialYearEndOf(iso: string): number {
    const year = Number(iso.slice(0, 4));
    return Number(iso.slice(5, 7)) >= 7 ? year + 1 : year;
}

interface Invoice {
    readonly id: string;
    readonly number: string | null;
    readonly reference: string | null;
    readonly status: string;
    readonly date: string | null;
    readonly dueDate: string | null;
    readonly subTotal: number;
    readonly totalTax: number;
    readonly total: number;
    readonly amountDue: number;
    readonly amountPaid: number;
    readonly currency: string | null;
}

interface InvoiceDetail extends Invoice {
    readonly lineItems: readonly {
        readonly description: string | null;
        readonly quantity: number | null;
        readonly unitAmount: number | null;
        readonly lineAmount: number | null;
        readonly accountCode: string | null;
        readonly taxType: string | null;
    }[];
}

interface InvoiceCopy {
    readonly id: number;
    readonly xeroInvoiceId: string;
    readonly invoiceNumber: string | null;
    readonly invoiceDate: string;
    readonly total: number;
    readonly hasPdf: boolean;
    readonly createdAt: string;
    readonly createdBy: string | null;
    readonly entries: number;
}

interface InvoiceList {
    readonly state: 'not_configured' | 'not_connected' | 'not_linked' | 'ok';
    readonly message: string | null;
    readonly invoices: readonly Invoice[];
    /** The client's earliest sales invoice in Xero; bounds the year dropdown. */
    readonly firstInvoiceDate: string | null;
}

function money(v: number | null): string {
    if (v === null) {
        return '';
    }
    const abs = Math.abs(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${v < 0 ? '-' : ''}$${abs}`;
}

function dmy(iso: string | null): string {
    if (iso === null) {
        return '';
    }
    const [y, m, d] = iso.split('-');
    return `${d ?? ''}-${m ?? ''}-${y ?? ''}`;
}

const STATUS_COLOUR: Record<string, 'success' | 'warning' | 'default' | 'info' | 'error'> = {
    PAID: 'success',
    AUTHORISED: 'warning',
    DRAFT: 'default',
    VOIDED: 'error',
};

function xeroUrl(id: string): string {
    return `https://go.xero.com/AccountsReceivable/View.aspx?InvoiceID=${id}`;
}

export function InvoicingTab({ clientId, companyName }: { clientId: number; companyName: string }): React.JSX.Element {
    const { me } = useAuth();
    const canConnect = me?.isSuperAdmin === true;
    const [list, setList] = useState<InvoiceList | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [connecting, setConnecting] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const thisYearEnd = currentFinancialYearEnd();
    const thisYearKey = `fy${String(thisYearEnd)}`;
    // Only the years this client can have invoices in: from the financial year of
    // its first Xero invoice (Xero history predates the old program, back to 2018
    // for some clients) up to this year. Remembered across year changes.
    const [firstYearEnd, setFirstYearEnd] = useState<number | null>(null);
    const oldest = Math.min(firstYearEnd ?? thisYearEnd, thisYearEnd);
    const periods: readonly Period[] = [
        ...Array.from({ length: thisYearEnd - oldest + 1 }, (_, i) => financialYear(thisYearEnd - i)),
        ALL_YEARS,
    ];
    const [periodKey, setPeriodKey] = useState(thisYearKey);
    const period = periods.find((p) => p.key === periodKey) ?? financialYear(thisYearEnd);
    const [search, setSearch] = useState('');
    const [openId, setOpenId] = useState<string | null>(null);
    const [detail, setDetail] = useState<InvoiceDetail | null>(null);
    const [detailError, setDetailError] = useState<string | null>(null);
    const [copies, setCopies] = useState<readonly InvoiceCopy[]>([]);
    const [pdfError, setPdfError] = useState<string | null>(null);
    const [xeroScopes, setXeroScopes] = useState<{ invoicesEnabled: boolean; payrollEnabled: boolean } | null>(null);

    // A connection made before Create Invoice / Send to Xero existed is still
    // "connected" and lists invoices fine, but cannot write. Only a reconnect fixes that.
    useEffect(() => {
        if (!canConnect) {
            return;
        }
        let cancelled = false;
        apiFetch<{ connected: boolean; invoicesEnabled: boolean; payrollEnabled: boolean }>('/xero/status')
            .then((s) => {
                if (!cancelled && s.connected) {
                    setXeroScopes({ invoicesEnabled: s.invoicesEnabled, payrollEnabled: s.payrollEnabled });
                }
            })
            .catch((err: unknown) => {
                console.warn('xero status:', err);
            });
        return () => {
            cancelled = true;
        };
    }, [canConnect]);

    useEffect(() => {
        let cancelled = false;
        apiFetch<{ copies: readonly InvoiceCopy[] }>(`/clients/${String(clientId)}/invoice-copies`)
            .then((r) => {
                if (!cancelled) {
                    setCopies(r.copies);
                }
            })
            .catch((err: unknown) => {
                // The Xero list is the main thing here; a missing copy list should not hide it.
                console.warn('invoice copies:', err);
            });
        return () => {
            cancelled = true;
        };
    }, [clientId]);

    const copyOf = new Map(copies.map((c) => [c.xeroInvoiceId.toLowerCase(), c]));

    function openPdf(copy: InvoiceCopy): void {
        setPdfError(null);
        apiFetch<{ url: string }>(`/clients/${String(clientId)}/invoice-copies/${String(copy.id)}/pdf`)
            .then((r) => {
                // A download (the link is signed with Content-Disposition), so the page stays put.
                window.location.assign(r.url);
            })
            .catch((err: unknown) => {
                setPdfError(err instanceof Error ? err.message : 'Could not open the saved PDF');
            });
    }

    const { from, to } = period;
    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams();
        if (from !== undefined) {
            params.set('from', from);
        }
        if (to !== undefined) {
            params.set('to', to);
        }
        const query = params.toString();
        apiFetch<InvoiceList>(`/clients/${String(clientId)}/invoices${query === '' ? '' : `?${query}`}`)
            .then((r) => {
                if (!cancelled) {
                    setList(r);
                    setError(null);
                    setPage(0);
                    if (r.firstInvoiceDate !== null) {
                        setFirstYearEnd(financialYearEndOf(r.firstInvoiceDate));
                    }
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setError(err instanceof Error ? err.message : 'Could not load invoices');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [clientId, from, to]);

    useEffect(() => {
        setDetail(null);
        setDetailError(null);
        if (openId === null) {
            return;
        }
        let cancelled = false;
        apiFetch<InvoiceDetail>(`/clients/${String(clientId)}/invoices/${openId}`)
            .then((d) => {
                if (!cancelled) {
                    setDetail(d);
                }
            })
            .catch((err: unknown) => {
                if (!cancelled) {
                    setDetailError(err instanceof Error ? err.message : 'Could not load the invoice');
                }
            });
        return () => {
            cancelled = true;
        };
    }, [clientId, openId]);

    function connect(): void {
        setConnecting(true);
        apiFetch<{ url: string }>('/xero/connect', { method: 'POST' })
            .then((r) => {
                window.location.assign(r.url);
            })
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not start the Xero connection');
                setConnecting(false);
            });
    }

    if (error !== null) {
        return <Alert severity="error">{error}</Alert>;
    }
    if (list === null) {
        return <Box sx={{ minHeight: 200 }} />;
    }

    if (list.state === 'not_configured') {
        return (
            <Alert severity="warning">
                Xero is not set up for the new portal yet. The Xero app&apos;s client ID and secret need adding to the
                API, and its redirect URL registering in the Xero developer portal.
            </Alert>
        );
    }
    if (list.state === 'not_connected') {
        return (
            <Alert
                severity="info"
                action={
                    canConnect ? (
                        <Button color="inherit" variant="outlined" size="small" onClick={connect} disabled={connecting}>
                            Connect Xero
                        </Button>
                    ) : undefined
                }
            >
                {list.message ?? 'Xero is not connected.'} {canConnect ? '' : 'Ask an administrator to connect Xero.'}
            </Alert>
        );
    }
    if (list.state === 'not_linked') {
        return (
            <Alert severity="info">
                {companyName} is not linked to a Xero contact, so there are no invoices to show. It needs its Xero
                ContactID set before invoices can be read.
            </Alert>
        );
    }

    const invoices = list.invoices;
    // Search every column as shown, plus the raw dates and amounts, so "30-06",
    // "2026-06", "INV-44", "PAID" and "6472" all match.
    const needle = search.trim().toLowerCase();
    const shown =
        needle === ''
            ? invoices
            : invoices.filter((i) =>
                  [
                      i.number,
                      i.reference,
                      i.status,
                      i.date,
                      i.dueDate,
                      dmy(i.date),
                      dmy(i.dueDate),
                      money(i.total),
                      money(i.amountDue),
                      String(i.total),
                      String(i.amountDue),
                  ].some((v) => v !== null && v.toLowerCase().includes(needle)),
              );
    const today = new Date().toISOString().slice(0, 10);
    const outstanding = invoices.filter((i) => i.status === 'AUTHORISED').reduce((s, i) => s + i.amountDue, 0);
    const overdue = invoices.filter((i) => i.status === 'AUTHORISED' && i.dueDate !== null && i.dueDate < today);
    const invoiced = invoices
        .filter((i) => i.status !== 'VOIDED' && i.status !== 'DRAFT')
        .reduce((s, i) => s + i.total, 0);

    const lastPage = Math.max(0, Math.ceil(shown.length / rowsPerPage) - 1);
    const currentPage = Math.min(page, lastPage);
    const pageRows = shown.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

    return (
        <Stack gap={2.5}>
            {xeroScopes !== null && (!xeroScopes.invoicesEnabled || !xeroScopes.payrollEnabled) ? (
                <Alert
                    severity="warning"
                    action={
                        <Button color="inherit" variant="outlined" size="small" onClick={connect} disabled={connecting}>
                            Reconnect Xero
                        </Button>
                    }
                >
                    Xero is connected, but without permission to{' '}
                    {[
                        xeroScopes.invoicesEnabled ? null : 'create invoices (Create Invoice)',
                        xeroScopes.payrollEnabled ? null : 'send timesheets (Send to Xero)',
                    ]
                        .filter((p) => p !== null)
                        .join(' or ')}
                    . Reconnect and approve the new permissions in Xero.
                </Alert>
            ) : null}
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                <StatCard
                    title="Invoiced"
                    icon={<Box />}
                    colour={ACCENTS.blue}
                    metrics={[{ label: `${String(invoices.length)} invoices`, value: money(invoiced) }]}
                />
                <StatCard
                    title="Outstanding"
                    icon={<Box />}
                    colour={ACCENTS.amber}
                    metrics={[{ label: 'Amount due', value: money(outstanding) }]}
                />
                <StatCard
                    title="Overdue"
                    icon={<Box />}
                    colour={overdue.length > 0 ? ACCENTS.red : ACCENTS.green}
                    metrics={[
                        {
                            label: `${String(overdue.length)} invoices`,
                            value: money(overdue.reduce((s, i) => s + i.amountDue, 0)),
                        },
                    ]}
                />
            </Box>

            <Paper sx={{ p: 2 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <Button
                        variant={periodKey === thisYearKey ? 'contained' : 'outlined'}
                        color={periodKey === thisYearKey ? 'primary' : 'inherit'}
                        onClick={() => {
                            setPeriodKey(thisYearKey);
                        }}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        This Year
                    </Button>
                    <TextField
                        select
                        size="small"
                        label="Financial year"
                        value={periodKey}
                        onChange={(e) => {
                            setPeriodKey(e.target.value);
                        }}
                        sx={{ minWidth: { md: 340 } }}
                    >
                        {periods.map((p) => (
                            <MenuItem key={p.key} value={p.key}>
                                {p.key === thisYearKey ? `${p.label} — this year` : p.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        size="small"
                        placeholder="Search invoice, reference, date, status or amount..."
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0);
                        }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'text.secondary' }} />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{ flex: 1 }}
                    />
                </Stack>
            </Paper>

            <Box>
                <TableContainer
                    sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}
                >
                    <Table sx={{ minWidth: 820 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice</TableCell>
                                <TableCell>Reference</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell>Due Date</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Total Inc GST</TableCell>
                                <TableCell align="right">Amount Due</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pageRows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                                        <Typography color="text.secondary">
                                            {needle !== ''
                                                ? `No invoices match "${search}".`
                                                : period.key === 'all'
                                                  ? `No invoices in Xero for ${companyName}.`
                                                  : `No invoices for ${companyName} in ${period.label}.`}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                pageRows.map((inv, idx) => (
                                    <TableRow
                                        key={inv.id}
                                        hover
                                        onClick={() => {
                                            setOpenId(inv.id);
                                        }}
                                        sx={{
                                            cursor: 'pointer',
                                            bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent',
                                        }}
                                    >
                                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                            {inv.number ?? '—'}
                                            {copyOf.has(inv.id.toLowerCase()) ? (
                                                <Chip
                                                    size="small"
                                                    variant="outlined"
                                                    color="info"
                                                    label="Copy saved"
                                                    sx={{ ml: 1 }}
                                                />
                                            ) : null}
                                        </TableCell>
                                        <TableCell>{inv.reference ?? ''}</TableCell>
                                        <TableCell>{dmy(inv.date)}</TableCell>
                                        <TableCell
                                            sx={{
                                                color:
                                                    inv.status === 'AUTHORISED' &&
                                                    inv.dueDate !== null &&
                                                    inv.dueDate < today
                                                        ? 'error.main'
                                                        : undefined,
                                            }}
                                        >
                                            {dmy(inv.dueDate)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                label={inv.status}
                                                color={STATUS_COLOUR[inv.status] ?? 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">{money(inv.total)}</TableCell>
                                        <TableCell align="right">{money(inv.amountDue)}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={shown.length}
                    page={currentPage}
                    onPageChange={(_e, p) => {
                        setPage(p);
                    }}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(Number.parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                />
            </Box>

            {copies.length > 0 ? (
                <Paper sx={{ p: 2 }}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                        Copies saved by the portal ({copies.length})
                    </Typography>
                    {pdfError !== null ? (
                        <Alert severity="error" sx={{ mb: 1 }}>
                            {pdfError}
                        </Alert>
                    ) : null}
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Invoice</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Entries</TableCell>
                                <TableCell align="right">Total Inc GST</TableCell>
                                <TableCell>Sent by</TableCell>
                                <TableCell align="right">PDF</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {copies.map((copy) => (
                                <TableRow key={copy.id}>
                                    <TableCell sx={{ fontWeight: 600 }}>{copy.invoiceNumber ?? '—'}</TableCell>
                                    <TableCell>{dmy(copy.invoiceDate)}</TableCell>
                                    <TableCell align="right">{copy.entries}</TableCell>
                                    <TableCell align="right">{money(copy.total)}</TableCell>
                                    <TableCell>{copy.createdBy ?? ''}</TableCell>
                                    <TableCell align="right">
                                        {copy.hasPdf ? (
                                            <IconButton
                                                size="small"
                                                aria-label={`Download ${copy.invoiceNumber ?? 'invoice'} PDF`}
                                                onClick={() => {
                                                    openPdf(copy);
                                                }}
                                            >
                                                <PictureAsPdfIcon fontSize="small" />
                                            </IconButton>
                                        ) : (
                                            <Typography
                                                component="span"
                                                color="text.secondary"
                                                sx={{ fontSize: '0.8rem' }}
                                            >
                                                Not saved
                                            </Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            ) : null}

            <Dialog
                open={openId !== null && (detail !== null || detailError !== null)}
                onClose={() => {
                    setOpenId(null);
                }}
                maxWidth="md"
                fullWidth
                slotProps={{ paper: { elevation: 1 } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    Invoice {detail?.number ?? ''}
                    {detail !== null ? (
                        <Chip
                            size="small"
                            variant="outlined"
                            label={detail.status}
                            color={STATUS_COLOUR[detail.status] ?? 'default'}
                        />
                    ) : null}
                    <Box sx={{ flex: 1 }} />
                    <IconButton
                        size="small"
                        aria-label="Close"
                        onClick={() => {
                            setOpenId(null);
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {detailError !== null ? <Alert severity="error">{detailError}</Alert> : null}
                    {detail !== null ? (
                        <Stack gap={2}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={3}>
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                        Client
                                    </Typography>
                                    <Typography>{companyName}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                        Reference
                                    </Typography>
                                    <Typography>{detail.reference ?? '—'}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Date</Typography>
                                    <Typography>{dmy(detail.date)}</Typography>
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                        Due Date
                                    </Typography>
                                    <Typography>{dmy(detail.dueDate)}</Typography>
                                </Box>
                            </Stack>
                            <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Description</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Unit Price</TableCell>
                                            <TableCell>Account</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {detail.lineItems.map((l, i) => (
                                            <TableRow key={i}>
                                                <TableCell sx={{ whiteSpace: 'pre-line' }}>
                                                    {l.description ?? ''}
                                                </TableCell>
                                                <TableCell align="right">{l.quantity ?? ''}</TableCell>
                                                <TableCell align="right">{money(l.unitAmount)}</TableCell>
                                                <TableCell>{l.accountCode ?? ''}</TableCell>
                                                <TableCell align="right">{money(l.lineAmount)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <Stack alignItems="flex-end" gap={0.5}>
                                <Typography>Subtotal {money(detail.subTotal)}</Typography>
                                <Typography>GST {money(detail.totalTax)}</Typography>
                                <Typography sx={{ fontWeight: 700 }}>Total {money(detail.total)}</Typography>
                                <Typography sx={{ color: 'text.secondary' }}>
                                    Paid {money(detail.amountPaid)}
                                </Typography>
                                <Typography sx={{ fontWeight: 700 }}>Amount due {money(detail.amountDue)}</Typography>
                            </Stack>
                        </Stack>
                    ) : null}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    {detail !== null && copyOf.get(detail.id.toLowerCase())?.hasPdf === true ? (
                        <Button
                            size="small"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => {
                                const copy = copyOf.get(detail.id.toLowerCase());
                                if (copy !== undefined) {
                                    openPdf(copy);
                                }
                            }}
                        >
                            Saved copy
                        </Button>
                    ) : null}
                    {detail !== null ? (
                        <Link
                            href={xeroUrl(detail.id)}
                            target="_blank"
                            rel="noopener"
                            sx={{ mr: 'auto', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                        >
                            Open in Xero <OpenInNewIcon fontSize="small" />
                        </Link>
                    ) : null}
                    <Button
                        onClick={() => {
                            setOpenId(null);
                        }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
}
